/**
 * Intent Detection Utility
 * Detects user intent from chat messages and extracts relevant entities
 */

export type IntentType = 'formulate' | 'score' | 'optimize' | 'product' | 'unknown';

export interface DetectedIntent {
  type: IntentType;
  confidence: number;
  extractedData?: {
    description?: string;
    productType?: string;
    productId?: string;
    recipeId?: string;
    objective?: string;
  };
}

/**
 * Detects intent from user message
 */
export const detectIntent = (message: string): DetectedIntent => {
  const lowerMessage = message.toLowerCase().trim();
  
  // Formulation intent keywords
  const formulationKeywords = [
    'formulate',
    'formulation',
    'create recipe',
    'make recipe',
    'ingredient formulation',
    'create formulation',
    'new formulation',
    'recipe creation'
  ];
  
  // Scoring intent keywords
  const scoringKeywords = [
    'calculate score',
    'calculate scores',
    'score calculation',
    'performance score',
    'supplier score',
    'new score',
    'get score',
    'show score',
    'score my product',
    'calculate supplier scores'
  ];
  
  // Optimization intent keywords
  const optimizationKeywords = [
    'optimize',
    'optimization',
    'optimise',
    'improve recipe',
    'enhance recipe',
    'better recipe',
    'optimize recipe',
    'recipe optimization'
  ];
  
  // Product generation intent keywords (already handled, but included for completeness)
  const productKeywords = [
    'generate product',
    'create product',
    'new product',
    'make product',
    'develop product'
  ];
  
  // Check for formulation intent
  const hasFormulationIntent = formulationKeywords.some(keyword => 
    lowerMessage.includes(keyword)
  );
  
  // Check for scoring intent
  const hasScoringIntent = scoringKeywords.some(keyword => 
    lowerMessage.includes(keyword)
  );
  
  // Check for optimization intent
  const hasOptimizationIntent = optimizationKeywords.some(keyword => 
    lowerMessage.includes(keyword)
  );
  
  // Check for product intent
  const hasProductIntent = productKeywords.some(keyword => 
    lowerMessage.includes(keyword)
  ) && !hasFormulationIntent; // Don't confuse formulation with product
  
  // Extract entities
  const extractedData: DetectedIntent['extractedData'] = {};
  
  // Extract description for formulation (everything after formulation keywords)
  if (hasFormulationIntent) {
    const formulationMatch = lowerMessage.match(/(?:formulate|formulation|create recipe|make recipe|ingredient formulation|create formulation|new formulation|recipe creation)\s+(.+)/);
    if (formulationMatch && formulationMatch[1]) {
      extractedData.description = formulationMatch[1].trim();
    } else {
      // Try to extract product type from common patterns
      const productTypeMatch = lowerMessage.match(/(?:for|a|an|the)\s+([a-z\s]+?)(?:\s|$)/);
      if (productTypeMatch && productTypeMatch[1]) {
        extractedData.description = productTypeMatch[1].trim();
      }
    }
  }
  
  // Extract product ID or name for scoring (basic pattern matching)
  if (hasScoringIntent) {
    // Look for product ID pattern (UUID or numeric)
    const productIdMatch = message.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    if (productIdMatch) {
      extractedData.productId = productIdMatch[1];
    }
  }
  
  // Extract recipe ID and objective for optimization
  if (hasOptimizationIntent) {
    // Look for recipe ID pattern
    const recipeIdMatch = message.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    if (recipeIdMatch) {
      extractedData.recipeId = recipeIdMatch[1];
    }
    
    // Extract objective
    if (lowerMessage.includes('lower sugar') || lowerMessage.includes('reduce sugar')) {
      extractedData.objective = 'lower_sugar';
    } else if (lowerMessage.includes('reduce cost') || lowerMessage.includes('lower cost')) {
      extractedData.objective = 'reduce_cost';
    } else if (lowerMessage.includes('clean label')) {
      extractedData.objective = 'clean_label';
    } else if (lowerMessage.includes('improve nutrition') || lowerMessage.includes('better nutrition')) {
      extractedData.objective = 'improve_nutrition';
    }
  }
  
  // Determine intent with confidence
  if (hasFormulationIntent) {
    return {
      type: 'formulate',
      confidence: 0.9,
      extractedData
    };
  }
  
  if (hasScoringIntent) {
    return {
      type: 'score',
      confidence: 0.9,
      extractedData
    };
  }
  
  if (hasOptimizationIntent) {
    return {
      type: 'optimize',
      confidence: 0.9,
      extractedData
    };
  }
  
  if (hasProductIntent) {
    return {
      type: 'product',
      confidence: 0.8,
      extractedData
    };
  }
  
  return {
    type: 'unknown',
    confidence: 0,
    extractedData
  };
};





